import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';

import FileCopyIcon from '@material-ui/icons/FileCopy';

import { Pagination } from 'components';
import ConfirmContext from 'contexts/ConfirmContext';

import { ACTIVITIES } from 'graphql/queries/activities';
import { DELETE_ACTIVITY } from 'graphql/mutations/activity';

import ScheduleDates from '../schedule-dates/ScheduleDates';

import { TrashIcon, EditIcon } from 'components/icons';

import {
  Typography,
  IconButton,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table
} from '@material-ui/core';

import useStyles from './styles';

const headingsConfig = classes => {
  return [
    { label: 'Photo', align: 'left', cellClassName: classes.imgCell },
    {
      label: 'Activity name',
      cellClassName: classes.nameCell,
      align: 'left'
    },
    {
      label: 'Status',
      cellClassName: classes.availabilityCell,
      align: 'left'
    },
    {
      label: 'Availability',
      cellClassName: classes.scheduleCell,
      align: 'left'
    },
    {
      label: 'Product code',
      cellClassName: classes.productCodeCell,
      align: 'left'
    },
    {
      label: 'Starting point',
      cellClassName: classes.startingPointCell,
      align: 'left'
    },
    { label: 'Actions', align: 'right' }
  ];
};

function ActivitiesList({ deleteCallback, ownerId }) {
  const classes = useStyles();
  const [t] = useTranslation();
  const history = useHistory();
  const confirm = React.useContext(ConfirmContext);
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = React.useState(0);

  const { data, refetch } = useQuery(ACTIVITIES, {
    variables: {
      businessOwnerId: ownerId,
      page: page
    }
  });
  const [deleteAccommodation] = useMutation(DELETE_ACTIVITY);

  React.useEffect(() => {
    refetch();
  }, []);

  const onDeleteHandler = item => {
    confirm({
      title: 'Delete Activity?',
      description: `${item.title} • ${item.productCode}`,
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() =>
        deleteAccommodation({ variables: { id: item.id } }).then(() => {
          deleteCallback();
          enqueueSnackbar(t('Activity was successfully removed!'), {
            variant: 'success'
          });
          if (data.activities.length <= 1 && page) {
            refetch({
              page: page - 1
            });
            setPage(page - 1);
          } else {
            refetch({ page });
          }
        })
      )
      .catch(() => {});
  };

  const onPageChangeHandler = (e, page) => {
    refetch({
      page: page - 1
    });
    setPage(page - 1);
  };

  return (
    <TableContainer component="div" classes={{ root: classes.container }}>
      <Table className={classes.table} aria-label="simple table">
        <TableHead className={classes.tableHead}>
          <TableRow>
            {headingsConfig(classes).map(heading => (
              <TableCell
                key={heading.label}
                align={heading.align}
                className={classNames(
                  classes.tableCell,
                  classes.headTableCell,
                  heading.cellClassName
                )}
              >
                <Typography variant="h3" className={classes.heading}>
                  {t(heading.label)}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data &&
            data.activities &&
            data.activities.map(activity => (
              <TableRow
                key={activity.id}
                classes={{ root: classes.tableRow }}
                onClick={() => history.push(`/activities/${activity.id}`)}
              >
                <TableCell
                  className={classNames(classes.tableCell, classes.imgCell)}
                  component="th"
                  scope="row"
                >
                  <div className={classes.imageWrapper}>
                    {activity.photo && (
                      <img
                        src={activity.photo}
                        alt=""
                        className={classes.image}
                      />
                    )}
                  </div>
                </TableCell>
                <TableCell
                  className={classNames(classes.tableCell, classes.nameCell)}
                  align="left"
                >
                  {activity.title}
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCell,
                    classes.availabilityCell
                  )}
                  align="left"
                >
                  {activity.availability ? (
                    <Typography color="primary">{t('Active')}</Typography>
                  ) : (
                    <Typography color="inherit">{t('Not active')}</Typography>
                  )}
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCell,
                    classes.scheduleCell
                  )}
                  align="left"
                >
                  <ScheduleDates item={activity} />
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCell,
                    classes.productCodeCell
                  )}
                  align="left"
                >
                  {activity.productCode}
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCell,
                    classes.startingPointCell
                  )}
                  align="left"
                >
                  {activity.startingPoint}
                </TableCell>
                <TableCell className={classes.tableCell} align="right">
                  <div style={{ display: 'inline-flex', paddingRight: '10px' }}>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        history.push(`/activities/${activity.id}/copy`);
                      }}
                      className={classes.iconButton}
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      aria-label="edit"
                    >
                      <FileCopyIcon
                        style={{
                          color: '#59758B',
                          width: '18px',
                          height: '18px'
                        }}
                      />
                    </IconButton>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        history.push(`/activities/${activity.id}`);
                      }}
                      className={classes.iconButton}
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      aria-label="edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteHandler(activity);
                      }}
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      className={classes.iconButton}
                      aria-label="delete"
                    >
                      <TrashIcon />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      {data && (
        <Pagination
          page={page + 1}
          count={data.numberOfActivitiesPages}
          onChange={onPageChangeHandler}
          amount={data.activities.length}
          total={data.accommodationsActivitiesOverview.activities.total}
        />
      )}
    </TableContainer>
  );
}

export default ActivitiesList;
